import React from 'react';
import { Document, Page, Text, View, StyleSheet, pdf, Font } from '@react-pdf/renderer';
import { CityTaxReport } from '../api.cityTax';

Font.register({
  family: 'Calibri',
  fonts: [
    { src: '/fonts/Calibri-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/Calibri-Bold.ttf',    fontWeight: 700 },
  ],
});


const s = StyleSheet.create({
  page:        { paddingTop: 60, paddingBottom: 40, paddingHorizontal: 60, fontSize: 9, fontFamily: 'Calibri' },
  officeAddr:  { fontSize: 9, marginBottom: 20 },
  title:       { fontSize: 13, fontWeight: 700, textAlign: 'center', marginBottom: 6 },
  subtitle:    { fontSize: 7.5, textAlign: 'center', color: '#555555', marginBottom: 20 },
  infoRow:     { flexDirection: 'row', marginBottom: 5 },
  infoLabel:   { width: 230, color: '#444444', fontSize: 9 },
  infoValue:   { flex: 1, fontWeight: 700, fontSize: 9 },
  sectionHead: { fontWeight: 700, fontSize: 10, marginBottom: 4 },
  small:       { fontSize: 7.5, color: '#555555', marginBottom: 8 },
  table:       { borderWidth: 1, borderColor: '#999999', marginTop: 8 },
  tableRow:    { flexDirection: 'row', borderBottomWidth: 1, borderColor: '#cccccc', paddingVertical: 5, paddingHorizontal: 8 },
  tableRowLast:{ flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 8 },
  tableCell:   { flex: 1, fontSize: 9 },
  tableVal:    { width: 90, textAlign: 'right', fontSize: 9 },
  paymentNote: { fontSize: 9, marginTop: 14 },
  signRow:     { flexDirection: 'row', justifyContent: 'space-between', marginTop: 48 },
  signBlock:   { width: 200 },
  signLine:    { paddingTop: 4, fontSize: 8, color: '#444444' },
  section:     { marginTop: 16 },
});

interface Props {
  report: CityTaxReport;
  month: string;
}

const CityTaxDocument: React.FC<Props> = ({ report, month }) => {
  const payerDisplay  = report.payerCompanyName || report.payerName || '';
  const taxableNights = report.adults?.nights ?? 0;
  const exemptNights  = (report.children?.nights ?? 0) + (report.disabled?.nights ?? 0);
  const totalAmount   = report.adults?.tax ?? 0;
  const fmtAmount = Number(totalAmount).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const payerRows: [string, string][] = [
    ['Jméno a příjmení ubytovatele (nebo název právnické osoby):', payerDisplay],
    ['Místo pobytu nebo sídlo právnícké osoby:', report.payerAddress || ''],
    ['IČO/DIČ:', report.payerIco || ''],
    ['Přidělený VS:', report.variableSymbol || ''],
    ['Kontaktní osoba:', report.payerName || ''],
    ['Telefonní číslo:', report.payerPhone || ''],
    ['E-mail:', report.payerEmail || ''],
    ['Období – kalendářní měsíc:', month],
  ];

  const taxRows: [string, string, boolean][] = [
    ['Celkový počet dnů osob, které podléhají poplatku', String(taxableNights), false],
    ['Celkový počet dnů osob, které jsou osvobozeny',    String(exemptNights),  false],
    ['Výše poplatku za osoby, které nejsou osvobozeny (Celkový počet dnů osob, které podléhají poplatku x sazba Kč 50,-)', `${fmtAmount} Kč`, true],
  ];

  return (
    <Document>
      <Page size="A4" style={s.page}>

        {/* Office header: bold name, address, databox ID */}
        {report.mailboxName && (
          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 10, fontWeight: 700 }}>{report.mailboxName}</Text>
            {report.mailboxAddress && <Text style={{ fontSize: 9 }}>{report.mailboxAddress}</Text>}
            {report.mailboxDataboxId && <Text style={{ fontSize: 9 }}>{'ID datové schránky: '}{report.mailboxDataboxId}</Text>}
          </View>
        )}

        <Text style={s.title}>HLÁŠENÍ</Text>
        <Text style={[s.title, { fontSize: 11, marginBottom: 6 }]}>k místnímu poplatku z pobytu</Text>
        <Text style={s.subtitle}>
          {'(dle §4 odst. 4 vyhlášky č. 19/2021 Sb. hl. m. Prahy, o místním poplatku z pobytu,\nv souladu se zákonem č. 565/1990 Sb., o místních poplatcích, ve znění pozdějších předpisů)'}
        </Text>

        <View style={s.section}>
          {payerRows.map(([label, value]) => (
            <View style={s.infoRow} key={label}>
              <Text style={s.infoLabel}>{label}</Text>
              <Text style={s.infoValue}>{value}</Text>
            </View>
          ))}
        </View>

        <View style={s.section}>
          <Text style={s.sectionHead}>Výpočet místního poplatku</Text>
          <Text style={{ fontSize: 9, color: '#555555', marginBottom: 8 }}>
            {'Sazba místního poplatku činí Kč 50,- za osobu a každý započatý den, s výjimkou dne počátku pobytu.\nPoplatek vybírá ubytovatel, který je povinen jej do 15. dne každého následujícího měsíce odvést správci místního poplatku.'}
          </Text>
          <Text style={{ fontSize: 9, color: '#555555' }}>
            <Text style={{ fontWeight: 700 }}>Adresa a název provozovny: </Text>
            {`${report.apartmentAddress || ''} – ${report.apartmentName || ''}`}
          </Text>
        </View>

        {report.basis === 'UBYPORT_FILED' && (
          <Text style={{ fontSize: 8, color: '#555555', marginTop: 8 }}>
            {'Podklad: pouze zahraniční hosté nahlášení do Ubyportu a čeští hosté; '}
            {`nezahrnuto hostů: ${report.excludedGuests ?? 0}.`}
          </Text>
        )}

        <View style={s.table}>
          {taxRows.map(([label, value, isLast], i) => (
            <View style={isLast ? s.tableRowLast : s.tableRow} key={i}>
              <Text style={s.tableCell}>{label}</Text>
              <Text style={[s.tableVal, isLast ? { fontWeight: 700 } : {}]}>{value}</Text>
            </View>
          ))}
        </View>

        <Text style={s.paymentNote}>
          {'Poplatek uhraďte na účet '}
          {report.mailboxName || ''}
          {', č. '}
          <Text style={{ fontWeight: 700 }}>{report.bankAccount || '_____________'}</Text>
          {', uvádějte prosím vždy s variabilním symbolem '}
          <Text style={{ fontWeight: 700 }}>{report.variableSymbol || '_____________'}</Text>
          {'.'}
        </Text>

        <View style={s.signRow}>
          <View style={s.signBlock}>
            <Text style={s.signLine}>V _________________ dne _____________</Text>
          </View>
          <View style={s.signBlock}>
            <Text style={s.signLine}>Podpis: ____________________</Text>
          </View>
        </View>

        <Text style={{ marginTop: 32, fontSize: 7, color: '#aaaaaa', textAlign: 'center' }}>
          Tento dokument byl vytvořen aplikací Guestbook.cz
        </Text>

      </Page>
    </Document>
  );
};

export const generateCityTaxPdf = async (
  report: CityTaxReport,
  month: string
): Promise<void> => {
  const blob = await pdf(<CityTaxDocument report={report} month={month} />).toBlob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `hlaseni-${month.replace('/', '-')}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
};
